/**
 * SSO Configuration for Azure AD Single Sign-On
 *
 * This module provides SSO capability detection and configuration.
 * When enabled, users who are already signed into their Microsoft account
 * will be automatically logged into the application.
 *
 * REQUIREMENTS:
 * 1. Set VITE_DOMAIN_HINT to your corporate domain (e.g., 'rbccoach.com')
 * 2. Azure AD App Registration must allow implicit flow for ID tokens
 * 3. Users must be signed into their Microsoft account in the browser
 */

import Logger from '../utils/logger.js';

/**
 * Get the corporate domain from environment variables
 */
const getCorporateDomain = () => {
  return import.meta.env.VITE_DOMAIN_HINT || import.meta.env.VITE_CORPORATE_DOMAIN || '';
};

/**
 * Check if the browser supports the features needed for SSO
 * SSO requires:
 * - sessionStorage (for MSAL cache)
 * - Ability to make cross-origin requests to login.microsoftonline.com
 */
const checkBrowserSupport = () => {
  try {
    // Check sessionStorage availability
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return false;
    }

    // Test sessionStorage access
    const testKey = '__sso_test__';
    sessionStorage.setItem(testKey, 'test');
    sessionStorage.removeItem(testKey);

    return true;
  } catch {
    return false;
  }
};

/**
 * Cache for domain detection to avoid repeated checks
 */
let domainDetectionCache = null;
let domainDetectionCacheTime = 0;
const DOMAIN_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * SSO Configuration object
 */
export const ssoConfig = {
  /**
   * The corporate domain for SSO (e.g., 'rbccoach.com')
   */
  get corporateDomain() {
    return getCorporateDomain();
  },

  /**
   * Check if SSO is technically possible in this environment
   * Returns true if:
   * - A corporate domain is configured
   * - Browser supports required features
   * - Not running in an iframe (which blocks SSO)
   */
  isSSOCapable: () => {
    // E2E hook: allow tests to opt out of the automatic silent-SSO attempt so the login
    // page renders its Sign In button immediately (ssoSilent otherwise blocks the UI for
    // the full iframe timeout in a headless context). This only disables the *automatic*
    // attempt — interactive sign-in is unaffected — so it is safe to leave in production.
    try {
      if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('e2e:disable-sso') === '1') {
        Logger.debug('[SSO] SSO auto-attempt disabled via e2e:disable-sso flag');
        return false;
      }
    } catch {
      /* sessionStorage may be unavailable — ignore and continue */
    }

    const domain = getCorporateDomain();
    if (!domain) {
      Logger.debug('[SSO] SSO not available: No corporate domain configured');
      return false;
    }

    if (!checkBrowserSupport()) {
      Logger.debug('[SSO] SSO not available: Browser does not support required features');
      return false;
    }

    // Check if running in an iframe (SSO doesn't work in sandboxed iframes)
    try {
      if (window.self !== window.top) {
        Logger.debug('[SSO] SSO not available: Running in iframe');
        return false;
      }
    } catch {
      // Cross-origin iframe - SSO won't work
      Logger.debug('[SSO] SSO not available: Cross-origin iframe detected');
      return false;
    }

    Logger.debug('[SSO] SSO is available for domain:', domain);
    return true;
  },

  /**
   * Detect if the current user appears to be on the corporate domain
   * This is a heuristic check - actual SSO will still be attempted regardless
   *
   * Checks:
   * 1. Previous successful login from this domain (cached in sessionStorage)
   * 2. Domain hint environment variable is set
   */
  isDomainJoined: async () => {
    // Return cached result if still valid
    const now = Date.now();
    if (domainDetectionCache !== null && now - domainDetectionCacheTime < DOMAIN_CACHE_TTL_MS) {
      return domainDetectionCache;
    }

    const domain = getCorporateDomain();
    if (!domain) {
      domainDetectionCache = false;
      domainDetectionCacheTime = now;
      return false;
    }

    try {
      // Check if we've had a previous successful login from this domain
      const previousUser = sessionStorage.getItem('sso_last_user_domain');
      if (previousUser === domain) {
        Logger.debug('[SSO] User previously authenticated from corporate domain');
        domainDetectionCache = true;
        domainDetectionCacheTime = now;
        return true;
      }

      // If domain hint is configured, assume the user might be domain-joined
      // The actual SSO attempt will confirm this
      if (domain) {
        Logger.debug('[SSO] Domain hint configured, assuming potential domain membership');
        domainDetectionCache = true;
        domainDetectionCacheTime = now;
        return true;
      }

      domainDetectionCache = false;
      domainDetectionCacheTime = now;
      return false;
    } catch (error) {
      Logger.warn('[SSO] Error checking domain membership:', error.message);
      domainDetectionCache = false;
      domainDetectionCacheTime = now;
      return false;
    }
  },

  /**
   * Clear the domain detection cache
   * Call this when network conditions change or after logout
   */
  clearDomainCache: () => {
    domainDetectionCache = null;
    domainDetectionCacheTime = 0;
    try {
      sessionStorage.removeItem('sso_last_user_domain');
    } catch {
      // Ignore sessionStorage errors
    }
  },

  /**
   * Record a successful SSO login for future detection
   * @param {string} userDomain - The domain portion of the user's email
   */
  recordSuccessfulSSO: userDomain => {
    try {
      if (userDomain) {
        sessionStorage.setItem('sso_last_user_domain', userDomain);
        domainDetectionCache = true;
        domainDetectionCacheTime = Date.now();
      }
    } catch {
      // Ignore sessionStorage errors
    }
  }
};

/**
 * SSO Silent Request configuration
 * Used for attempting silent authentication without user interaction
 */
export const getSSOSilentRequest = () => {
  const clientId = import.meta.env.VITE_AZURE_CLIENT_ID;
  const domain = getCorporateDomain();

  return {
    scopes: [`api://${clientId}/access_api`],
    // Don't specify prompt - let MSAL handle it for silent flow
    ...(domain && {
      extraQueryParameters: {
        domain_hint: domain
      }
    })
  };
};

// Legacy export for backward compatibility
export const ssoSilentRequest = getSSOSilentRequest();

// Log SSO configuration status on module load
if (ssoConfig.isSSOCapable()) {
  Logger.info('[SSO] Single Sign-On is ENABLED for domain:', getCorporateDomain());
} else {
  Logger.debug('[SSO] Single Sign-On is not available in this environment');
}
